<template>
  <div>
    <div>
      <router-view name="notifications" />
    </div>
    <router-view />
    <div class="tw-pointer-events-none tw-fixed tw-top-5 tw-z-[1000] tw-flex tw-w-full tw-flex-col tw-items-center">
      <div class="tw-max-w-[70vw]">
        <div v-for="toast in toasts" :key="toast.id" class="tw-mb-2 tw-flex">
          <StudioToast
            v-if="toast"
            :mode="toast.mode"
            :message="toast.msg"
            :delay="toast.duration"
            :action="toast.action"
            @close="removeToast(toast.id)"
          />
        </div>
      </div>
    </div>
    <ContentLabToast />
  </div>
</template>

<script>
import logger from '@goldcast/logger';
import { Hub } from 'aws-amplify';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import store from './store';

import { trackMouseClick } from '@/analytics';
import ContentLabToast from '@/studio/contentLab/ContentLabToast.vue';
import StudioToast from '@/studioComponents/StudioToast.vue';
import { getEnv } from '@/utils/env';

import('@/../tailwind/tailwind-output.css');

export default {
  name: 'App',
  components: { StudioToast, ContentLabToast },
  data() {
    return {
      is_zendesk_enabled: getEnv('VUE_APP_ZENDESK_KEY') !== 'false',
      is_sf_script_loaded: false,
      is_sf_ready: false,
      sf_launch_pending: false,
      sfReadyHandlerRegistered: false
    };
  },
  created() {
    this.watchFutureAuthStateChangeOfUser();
    this.addZendeskScript();
    this.addSFScript();
    window.addEventListener('click', trackMouseClick);
    window.addEventListener('openSupportChat', this.handleSupportChatRequest);
  },
  beforeUnmount() {
    window.removeEventListener('click', trackMouseClick);
    window.removeEventListener('openSupportChat', this.handleSupportChatRequest);
  },
  computed: {
    ...mapGetters('org', {
      org: 'getCurrentOrg'
    }),
    ...mapGetters('user', {
      currentUser: 'getCurrentUser'
    }),
    ...mapGetters('zendeskWidget', ['isThereNoLiveEventsInOrg']),
    ...mapState('studioToast', { toasts: 'toasts' })
  },
  watch: {
    currentUser(value) {
      if (value) {
        this.addSFScript();
      }
      if (value && this.is_zendesk_enabled && !this.org?.useSalesforceChatInProduct) {
        const zenDeskScript = document.getElementById('ze-snippet');
        if (!zenDeskScript) {
          this.addZendeskScript();
        } else {
          this.showZendeskWidget();
        }
      } else {
        if (window.zE) {
          window.zE('webWidget', 'hide');
        }
        const LOGIN_ROUTES = ['Login', 'Oktalogin', 'Ssologin'];
        if (!value && !LOGIN_ROUTES.includes(this.$route.name)) {
          this.$router.push({ name: 'Login' }).catch(e => {
            logger.error('[App] Error in redirecting to login page: ', e);
          });
        }
      }
    },
    org() {
      this.addSFScript();
      this.setZendeskSettings();
      this.showZendeskWidget();
    }
  },
  methods: {
    ...mapActions('user', ['resetUser']),
    ...mapMutations('studioToast', ['removeToast']),
    watchFutureAuthStateChangeOfUser() {
      Hub.listen('auth', capsule => {
        const event = capsule.payload.event;
        if (event === 'signOut') {
          this.resetUser();
        }
      });
    },
    handleSupportChatRequest() {
      if (this.org?.useSalesforceChatInProduct) {
        this.openSFChat();
      } else if (window.zE) {
        window.zE('webWidget', 'show');
        window.zE('webWidget', 'open');
      }
    },
    async watchZendeskWidgetState() {
      if (window.zE) {
        const self = this;
        window.zE('webWidget:on', 'open', function () {
          store.commit('zendeskWidget/setZendeskChatState', true);
        });
        window.zE('webWidget:on', 'userEvent', function (event) {
          if (event.action === 'Chat Shown') {
            if (!self.isThereNoLiveEventsInOrg) {
              window.zE('webWidget', 'chat:addTags', ['live_event']);
            } else {
              window.zE('webWidget', 'chat:removeTags', ['live_event']);
            }
          } else if (event.action === 'Web Widget Minimised') {
            store.commit('zendeskWidget/setZendeskChatState', false);
          }
        });
      }
    },
    addZendeskScript() {
      if (this.is_zendesk_enabled && this.currentUser && !this.org?.useSalesforceChatInProduct) {
        const externalScript = document.createElement('script');
        externalScript.onload = () => {
          this.watchZendeskWidgetState();
          this.setZendeskSettings();
          this.identifyZendeskUser();
          this.showZendeskWidget();
        };
        externalScript.setAttribute(
          'src',
          `https://static.zdassets.com/ekr/snippet.js?key=${getEnv('VUE_APP_ZENDESK_KEY')}`
        );
        externalScript.setAttribute('id', 'ze-snippet');
        document.head.appendChild(externalScript);
      }
    },
    addSFScript() {
      if (this.is_sf_script_loaded || !this.currentUser || !this.org?.useSalesforceChatInProduct) {
        return;
      }
      this.is_sf_script_loaded = true;

      window.initEmbeddedMessaging = () => {
        try {
          window.embeddedservice_bootstrap.settings.language = 'en_US';
          window.embeddedservice_bootstrap.init(
            '00D00000000hXfW',
            'Customer_Support_Goldcast_Enhanced_Chat_In_Product',
            'https://cvent.my.site.com/ESWCustomerSupportGoldc1784111865290',
            { scrt2URL: 'https://cvent.my.salesforce-scrt.com' }
          );
        } catch (err) {
          logger.error('[SF] Error loading Embedded Messaging: ', err);
        }
      };

      if (!this.sfReadyHandlerRegistered) {
        this.sfReadyHandlerRegistered = true;
        window.addEventListener('onEmbeddedMessagingReady', () => {
          const user = this.currentUser;
          try {
            window.embeddedservice_bootstrap.utilAPI.hideChatButton();
          } catch (e) {
            logger.warn('[SF] hideChatButton not available yet:', e);
          }
          try {
            window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
              Product_Type: 'Goldcast',
              First_Name: user.first_name,
              Last_Name: user.last_name,
              Email: user.email
            });
          } catch (e) {
            logger.warn('[SF] setHiddenPrechatFields failed:', e);
          }
          this.is_sf_ready = true;
          if (this.sf_launch_pending) {
            this.sf_launch_pending = false;
            this.launchSFChat();
          }
        });
      }

      const script = document.createElement('script');
      script.src = 'https://cvent.my.site.com/ESWCustomerSupportGoldc1784111865290/assets/js/bootstrap.min.js';
      script.onload = window.initEmbeddedMessaging;
      script.onerror = () => {
        logger.error('[SF] Failed to load Salesforce bootstrap script');
        this.is_sf_script_loaded = false;
      };
      document.head.appendChild(script);
    },
    openSFChat() {
      if (this.is_sf_ready) {
        this.launchSFChat();
      } else {
        logger.warn('[SF] Widget not ready yet — queuing launch');
        this.sf_launch_pending = true;
      }
    },
    launchSFChat() {
      try {
        window.embeddedservice_bootstrap.utilAPI
          .launchChat()
          .then(() => logger.info('[SF] Chat launched successfully'))
          .catch(err => logger.error('[SF] launchChat failed:', err));
      } catch (e) {
        logger.error('[SF] Error calling launchChat:', e);
      }
    },
    showZendeskWidget() {
      if (window.zE) {
        window.zE('webWidget:on', 'close', function () {
          window.zE('webWidget', 'hide');
        });
        window.zE('webWidget', 'hide');
      }
    },
    setZendeskSettings() {
      if (this.org && window.zE) {
        const isContentLabStandalone = this.org.is_content_lab_standalone;
        window.zE('webWidget', 'updateSettings', {
          webWidget: {
            chat: {
              suppress: isContentLabStandalone
            },
            contactForm: {
              suppress: !isContentLabStandalone
            }
          }
        });
        const org = this.org;
        window.zE(function () {
          window.zE('webWidget', 'chat:addTags', [org.name]);
        });
      }
    },
    identifyZendeskUser() {
      const self = this;
      if (window.zE) {
        window.zE(function () {
          window.zE.identify({
            name: self.currentUser.first_name.concat(' ', self.currentUser.last_name),
            email: self.currentUser.email
          });
        });
      }
    }
  }
};
</script>

<style>
body {
  --background: 255 255 255;
  --accent: 241 245 249;
  --muted: 241 245 249;
  --muted-foreground: 71 85 105;
  --foreground: 2 6 23;
  --success: 22 163 74;
  --danger: 220 38 38;
}

#embeddedMessagingConversationButton,
.embeddedMessagingConversationButton {
  display: none !important;
}
</style>
